import { useFormikContext } from 'formik';
import { TranslationKey } from 'modules/i18n';
import { CVBuilderFormValues } from 'utils/cv-form-db';
import { useCvBuilderData } from '../data/CvBuilderDataContext';
import {
  getNextStep,
  getPreviousStep,
  isProgressStepKey,
  StepKey
} from '../stepMeta';
import { arePersonalDetailsValid } from '../steps/PersonalDetails/utils';
import { validateCVForm } from '../utils';
import { useStepNavigate } from './useStepNavigate';

interface StepGate {
  isComplete: () => boolean;
  /**
   * Why forward navigation is blocked, rendered under the disabled Next button. Only the
   * gating steps need one — a step that can never block (`isComplete: () => true`) has
   * nothing to explain.
   */
  reasonKey?: TranslationKey;
}

export const useStepValidation = (stepKey: StepKey) => {
  const stepNavigate = useStepNavigate();
  const { saveDraft } = useCvBuilderData();
  const formikContext = useFormikContext<CVBuilderFormValues>();

  // Defensive programming to handle missing context in production
  if (!formikContext) {
    console.warn('useStepValidation: Formik context not available');
    return {
      isStepComplete: false,
      blockedReasonKey: undefined,
      hasPreviousStep: false,
      handleStepNavigation: async () => {},
      handlePreviousStepNavigation: async () => {},
      formErrors: {}
    };
  }

  const { values, setFieldValue } = formikContext;

  /**
   * The gating steps mirror pro-core's `CvBuilderStep.requiresData` and its submit gate
   * `CvService.assertSubmittable` (name + email, >= 1 education entry, >= 1 language
   * skill). Personal details is deliberately stricter here — the wizard asks for all nine
   * fields — which is safe: a stricter client can only block earlier than the server,
   * never let through something the server would reject.
   *
   * Every other step is always navigable; optional steps stay optional. The same fact in
   * structural form lives in `stepMeta.requiresData`.
   */
  const stepGates: Record<StepKey, StepGate> = {
    LANGUAGE: {
      isComplete: () => !!values.language && values.language.trim() !== '',
      reasonKey: 'cvBuilderPage.gate.language'
    },
    OVERVIEW: { isComplete: () => true },
    PERSONAL_DETAILS: {
      isComplete: () => arePersonalDetailsValid(values),
      reasonKey: 'cvBuilderPage.gate.personalDetails'
    },
    EDUCATION: {
      isComplete: () => (values.education?.length ?? 0) > 0,
      reasonKey: 'cvBuilderPage.gate.education'
    },
    PROFESSIONAL_EXPERIENCE: { isComplete: () => true },
    HARD_SKILLS: { isComplete: () => true },
    SOFT_SKILLS: { isComplete: () => true },
    LANGUAGE_SKILLS: {
      isComplete: () => (values.languageSkills?.length ?? 0) > 0,
      reasonKey: 'cvBuilderPage.gate.languageSkills'
    },
    CERTIFICATES: { isComplete: () => true },
    SUMMARY: { isComplete: () => true }
  };

  const isStepComplete = stepGates[stepKey].isComplete();

  const handleStepNavigation = async () => {
    const nextStep = getNextStep(stepKey);
    if (!nextStep) {
      return; // terminal step (SUMMARY) — no forward transition
    }

    // Acknowledge the current step when navigating forward. Only progress-contributing
    // steps are acknowledged: this set travels to pro-core as `completedSteps` and is the
    // numerator of the operator funnel's progress percentage, so it must never start
    // including the navigation-only steps.
    const completedSteps = values.completedSteps || [];
    const updatedCompletedSteps = [...completedSteps];
    if (isProgressStepKey(stepKey) && !completedSteps.includes(stepKey)) {
      updatedCompletedSteps.push(stepKey);
    }

    // Update local Formik state (instant), then fire the draft sync and navigate.
    await setFieldValue('completedSteps', updatedCompletedSteps);

    // Fire-and-forget durability: navigation NEVER waits on the sync, so a slow/failed
    // background save can't strand the user. The data port owns persistence + error
    // handling (IndexedDB for authenticated; single-flight network + 403→expired for
    // guest) and saveDraft never rejects.
    void saveDraft({ ...values, completedSteps: updatedCompletedSteps });

    stepNavigate(nextStep.slug);
  };

  const handlePreviousStepNavigation = () => {
    const previousStep = getPreviousStep(stepKey);
    if (!previousStep) {
      return;
    }

    void saveDraft(values);
    stepNavigate(previousStep.slug);
  };

  return {
    isStepComplete,
    /** Set only while the step is incomplete, so callers can render it unconditionally. */
    blockedReasonKey: isStepComplete ? undefined : stepGates[stepKey].reasonKey,
    /** False for the intro/overview/first data step — the Back button hides itself. */
    hasPreviousStep: !!getPreviousStep(stepKey),
    handleStepNavigation,
    handlePreviousStepNavigation,
    formErrors: validateCVForm(values)
  };
};
